import { PAYMENT_MODES, ORDER_TYPES } from '../constants/pos.js';
import { formatInZone } from '../time/index.js';
import { buildUpiUri } from './upi.js';
import { formatAmount, rateLabel } from './calc.js';

const modeLabel = (k) => PAYMENT_MODES.find((m) => m.key === k)?.label ?? k;
const orderTypeLabel = (k) => ORDER_TYPES.find((o) => o.key === k)?.label ?? k;

/**
 * Receipt view-model built from a bill's immutable snapshot. Used by the browser receipt
 * (print + public link) and, from Phase 3, by the ESC/POS builder — so every receipt says
 * the same thing regardless of how it's printed.
 *
 * kind: 'invoice' (completed bill) | 'proforma' (held order, pre-bill); credit notes use
 * buildCreditNoteModel. duplicate: true on reprints (printed as "DUPLICATE").
 */
export function buildReceiptModel(bill, { receiptUrl = null, kind, upi, duplicate = false } = {}) {
  const snap = bill.snapshot ?? {};
  const biz = snap.business ?? {};
  const outlet = snap.outlet ?? {};
  const opts = snap.receipt ?? {};
  const tz = outlet.timezone ?? 'Asia/Kolkata';
  const resolvedKind = kind ?? (bill.status === 'held' ? 'proforma' : 'invoice');
  const t = bill.totals;
  const a = (p) => formatAmount(p);

  const title =
    resolvedKind === 'proforma'
      ? 'PRE-BILL (NOT A TAX INVOICE)'
      : biz.gstin || outlet.gstin
        ? 'TAX INVOICE'
        : 'BILL OF SUPPLY';

  const totalRows = [{ label: 'Item total', value: a(t.grossPaise) }];
  if (t.discountPaise) totalRows.push({ label: 'Discount', value: `-${a(t.discountPaise)}` });
  for (const c of bill.charges ?? []) totalRows.push({ label: c.label, value: a(c.amountPaise) });
  // GST-inclusive prices already contain the tax: listing CGST/SGST as rows would read as if
  // they were added on top. Only tax actually added (exclusive prices) becomes a total row;
  // included tax is stated in a note (the tax breakup table has the split).
  const chargeBase = (bill.charges ?? []).reduce((s, c) => s + (c.amountPaise ?? 0), 0);
  const added =
    t.subtotalPaise != null && t.itemsNetPaise != null
      ? Math.max(0, Math.min(t.taxPaise ?? 0, t.subtotalPaise - t.itemsNetPaise - chargeBase))
      : (t.taxPaise ?? 0);
  const included = Math.max(0, (t.taxPaise ?? 0) - added);
  if (included === 0) {
    if (t.cgstPaise) totalRows.push({ label: 'CGST', value: a(t.cgstPaise) });
    if (t.sgstPaise) totalRows.push({ label: 'SGST', value: a(t.sgstPaise) });
    if (t.igstPaise) totalRows.push({ label: 'IGST', value: a(t.igstPaise) });
    if (t.cessPaise) totalRows.push({ label: 'Cess', value: a(t.cessPaise) });
  } else if (added > 0) {
    totalRows.push({ label: 'GST added', value: a(added) });
  }
  if (t.roundOffPaise) totalRows.push({ label: 'Round off', value: a(t.roundOffPaise) });
  const taxNote = included > 0 ? `Prices include GST of ${a(included)}` : null;

  const due = bill.duePaise ?? 0;
  const unpaid = resolvedKind === 'proforma' ? t.grandTotalPaise : due;
  let upiUri = null;
  if (opts.showUpiQr !== false && unpaid > 0 && upi?.vpa) {
    try {
      upiUri = buildUpiUri({
        vpa: upi.vpa,
        payeeName: upi.payeeName ?? biz.name,
        amountPaise: unpaid,
        note: bill.invoiceNo ? `Bill ${bill.invoiceNo}` : `Order ${bill.tokenNo ?? ''}`.trim(),
        ref: bill.invoiceNo ?? String(bill.clientUuid ?? bill.id ?? '').slice(0, 12),
      });
    } catch {
      upiUri = null;
    }
  }

  return {
    kind: resolvedKind,
    title,
    status: bill.status,
    watermark: bill.status === 'voided' ? 'VOID' : bill.status === 'refunded' ? 'REFUNDED' : null,
    copyLabel: duplicate ? 'DUPLICATE' : null,
    business: { name: biz.name, legalName: biz.legalName, logoUrl: biz.logoUrl ?? null },
    outlet: {
      name: outlet.name,
      addressLines: [
        outlet.address?.line1,
        outlet.address?.line2,
        [outlet.address?.city, outlet.address?.pincode].filter(Boolean).join(' - '),
      ].filter(Boolean),
      phone: outlet.phone ?? null,
    },
    gstin: opts.showGstin !== false ? (outlet.gstin ?? biz.gstin ?? null) : null,
    fssai: opts.showFssai !== false ? (outlet.fssaiNo ?? biz.fssaiNo ?? null) : null,
    headerLines: opts.headerLines ?? [],
    footerLines: opts.footerLines ?? [],
    meta: [
      bill.invoiceNo && { label: 'Bill No', value: bill.invoiceNo },
      { label: 'Date', value: formatInZone(bill.invoiceDate ?? bill.createdAt, tz, 'dd/MM/yyyy hh:mm a') },
      bill.tokenNo != null && { label: 'Token', value: String(bill.tokenNo) },
      bill.tableNo && { label: 'Table', value: bill.tableNo },
      { label: 'Type', value: orderTypeLabel(bill.orderType) },
      opts.showCashier !== false && bill.completedBy?.name && { label: 'Cashier', value: bill.completedBy.name },
      opts.showCustomer !== false && bill.customer?.name && { label: 'Customer', value: bill.customer.name },
      opts.showCustomer !== false && bill.customer?.phone && { label: 'Phone', value: bill.customer.phone },
      bill.customer?.gstin && { label: 'Cust. GSTIN', value: bill.customer.gstin },
    ].filter(Boolean),
    lines: (bill.lines ?? []).map((l) => ({
      name: l.variantName ? `${l.name} (${l.variantName})` : l.name,
      modifiers: (l.modifiers ?? []).map((m) => m.name),
      note: l.note ?? null,
      qty: l.qty,
      rate: a(l.unitPricePaise),
      amount: a(l.amounts.grossPaise - l.amounts.lineDiscountPaise),
      foodType: l.foodType,
    })),
    totals: totalRows,
    grandTotal: a(t.grandTotalPaise),
    grandTotalPaise: t.grandTotalPaise,
    taxNote,
    taxBreakup:
      opts.showTaxBreakup !== false
        ? (bill.taxBreakup ?? [])
            .filter((r) => r.gstRateBp || r.cessRateBp)
            .map((r) => ({
              rate: rateLabel(r.gstRateBp) + (r.cessRateBp ? ` + cess ${rateLabel(r.cessRateBp)}` : ''),
              taxable: a(r.taxablePaise),
              cgst: r.cgstPaise ? a(r.cgstPaise) : null,
              sgst: r.sgstPaise ? a(r.sgstPaise) : null,
              igst: r.igstPaise ? a(r.igstPaise) : null,
              cess: r.cessPaise ? a(r.cessPaise) : null,
            }))
        : [],
    payments: (bill.payments ?? []).map((p) => ({
      label: modeLabel(p.mode) + (p.reference ? ` (${p.reference})` : ''),
      amount: a(p.amountPaise),
      tendered: p.tenderedPaise ? a(p.tenderedPaise) : null,
      change: p.changePaise ? a(p.changePaise) : null,
    })),
    due: due > 0 ? a(due) : null,
    upiUri,
    receiptUrl,
    poweredBy: biz.showPoweredBy !== false,
    paperWidth: opts.paperWidth ?? '80mm',
    // Template options for thermal printing (Settings → Receipt template).
    options: {
      showLogo: opts.showLogo !== false,
      showLinkQr: Boolean(opts.showLinkQr),
      fontSize: opts.fontSize === 'large' ? 'large' : 'normal',
    },
  };
}

const REFUND_MODE_LABELS = {
  cash: 'Cash',
  upi: 'UPI',
  card: 'Card',
  wallet: 'Wallet',
  due_adjust: 'Adjusted against due',
};

/**
 * Credit note (refund) view-model. Same header as the invoice it reverses, so printed and
 * on-screen credit notes look like the original bill.
 */
export function buildCreditNoteModel(bill, cn) {
  const base = buildReceiptModel({ ...bill, payments: [], duePaise: 0 }, { kind: 'invoice' });
  const tz = bill.snapshot?.outlet?.timezone ?? 'Asia/Kolkata';
  const t = cn.totals ?? {};
  const a = (p) => formatAmount(p);
  const totals = [{ label: 'Taxable value', value: a(t.taxablePaise ?? 0) }];
  if (t.cgstPaise) totals.push({ label: 'CGST', value: a(t.cgstPaise) });
  if (t.sgstPaise) totals.push({ label: 'SGST', value: a(t.sgstPaise) });
  if (t.igstPaise) totals.push({ label: 'IGST', value: a(t.igstPaise) });
  if (t.cessPaise) totals.push({ label: 'Cess', value: a(t.cessPaise) });
  for (const c of cn.charges ?? []) totals.push({ label: c.label, value: a(c.totalPaise ?? c.amountPaise ?? 0) });
  if (t.roundOffPaise) totals.push({ label: 'Round off', value: a(t.roundOffPaise) });
  return {
    ...base,
    kind: 'credit_note',
    title: 'CREDIT NOTE',
    watermark: null,
    headerLines: [],
    meta: [
      { label: 'CN No', value: cn.number },
      { label: 'Date', value: formatInZone(cn.date, tz, 'dd/MM/yyyy hh:mm a') },
      { label: 'Against', value: cn.invoiceNo ?? bill.invoiceNo },
      { label: 'Bill date', value: formatInZone(bill.invoiceDate ?? bill.createdAt, tz, 'dd/MM/yyyy') },
      bill.customer?.name && { label: 'Customer', value: bill.customer.name },
      bill.customer?.gstin && { label: 'Cust. GSTIN', value: bill.customer.gstin },
      { label: 'Reason', value: cn.reason },
    ].filter(Boolean),
    lines: (cn.lines ?? []).map((l) => ({
      name: l.name,
      modifiers: [],
      note: null,
      qty: l.qty,
      rate: a(Math.round((l.amounts?.totalPaise ?? 0) / Math.max(1, l.qty))),
      amount: a(l.amounts?.totalPaise ?? 0),
      foodType: null,
    })),
    totals,
    grandTotal: a(t.totalPaise ?? 0),
    grandTotalPaise: t.totalPaise ?? 0,
    grandTotalLabel: 'REFUND',
    taxNote: null,
    taxBreakup: [],
    payments: [
      { label: `Refund: ${REFUND_MODE_LABELS[cn.refundMode] ?? cn.refundMode}`, amount: a(t.totalPaise ?? 0) },
    ],
    due: null,
    upiUri: null,
    receiptUrl: null,
    footerLines: [cn.approvedBy ? `Approved by ${cn.approvedBy}` : null].filter(Boolean),
  };
}
